import { useConnection, Node, NodeProps, useNodeConnections, useReactFlow } from "@xyflow/react"
import CustomHandle from "../CustomHandle"
import TargetHandle from "../TargetHandle"
import { cn } from "@/lib/utils"
import { useSnapshot } from "valtio"
import { store } from "@/stores/stores"
import { Divider, Tooltip } from "@mantine/core"
import PanelCard from "../PanelCard"
import PanelButton from "../PanelButton"
import { useHover } from "@mantine/hooks"
import { handleNodesRemove } from "../flow.utils"
import { useEffect } from "react"
import { ScenarioNodeData } from "@/stores/scenario.store"
import { NodeCategory } from "@/nodes"

export type BaseNodeType = Node<ScenarioNodeData & Record<string, unknown>, any>

function BaseNode(props: NodeProps<BaseNodeType> & { draggingMode?: boolean; style?: React.CSSProperties; className?: string }) {
  const snap = useSnapshot(store)
  const connection = useConnection()
  const { hovered, ref } = useHover()

  const node = store.scenario.nodeDefinitions.record[props.data?.node_type]
  const isTrigger = node.category === NodeCategory.TRIGGER || props.data?.action?.startsWith("trigger::")
  const isConnectable = connection?.fromNode?.id !== props.id && !isTrigger
  const isHoveringThisNode = isConnectable && connection.inProgress && connection.toNode?.id === props.id
  const isConnecting = connection.inProgress && connection.fromNode?.id === props.id
  const shouldBeTransparent =
    !!snap.scenario.editing.selectedNode &&
    snap.scenario.editing.selectedNode?.node_type !== "add-action" &&
    snap.scenario.editing.selectedNode?.node?.id !== props.id

  const connections = useNodeConnections({
    handleType: "source",
  })
  const isConnected = connections.length > 0 || isConnecting
  const isDeleted = snap.scenario.editing.deletedNodes?.[props.id]

  const selecteNode = () => {
    store.scenario.actions.setSelectedNode({ node_type: props.data.node_type, node: props as any })
  }

  const onClickNode = (e) => {
    if (e.ctrlKey) {
      return
    } else {
      selecteNode()
    }
  }

  useEffect(() => {
    if (!props.selected && store.scenario.editing.selectedNode?.node?.id === props.id) {
      store.scenario.actions.setSelectedNode(null)
    }

    // if (props.selected && !store.scenario.editing.selectedNode?.node?.id && !connection.inProgress) {
    //   selecteNode()
    // }
  }, [props.selected])

  return (
    <div
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-0.5 transition-opacity duration-150",
        !isConnectable && connection.inProgress && connection.fromNode?.id !== props.id && "pointer-events-none opacity-50",
        shouldBeTransparent && "opacity-50",
        isDeleted && "animate-scale-out animate-duration-150",
      )}
      ref={ref}
    >
      <div className="g_node relative rounded-lg outline-[0px] outline-transparent outline-dashed">
        <div className={cn("relative rounded-lg bg-transparent p-1 transition-colors duration-125")}>
          <div
            className={cn(
              `animate-scale-in relative fc size-20 !cursor-pointer rounded-full border-[5px] border-background text-white shadow-node outline-[0px]
              transition-all duration-125 ease-in-out animate-duration-300 [&_g\_handle\_circle]:bg-emerald-500 [&_svg]:size-8`,
              isHoveringThisNode && "outline-[5px] outline-emerald-500",
              !isTrigger && "rounded-4xl",
              props.className,
            )}
            style={{ ...props.style, backgroundColor: node.color }}
            onClick={onClickNode}
          >
            <div className={cn("fc", snap.scenario.editing.draggingNode && "pointer-events-none")}>
              {/* {(action as any).iconElement ? <action.iconElement /> : <img src={action.image_src} className="size-8" />} */}
              {node.image_src && <img src={node.image_src} className="size-8" />}
            </div>
          </div>
          <div className={cn("fc", snap.scenario.editing.draggingNode && "pointer-events-none")}>
            {!props.draggingMode && (
              <>
                {isTrigger && <IconMageZapFill className={`absolute -left-7 top-7 size-7 text-yellow-400`} />}
                {node.category !== NodeCategory.TRIGGER && <TargetHandle isConnectable={isConnectable} />}
              </>
            )}
          </div>
        </div>
        {!props.draggingMode && (
          <div className={cn("absolute top-0 h-full", isConnected ? "!-right-8" : "-right-4")}>
            <div className={cn("flex h-full flex-col justify-center gap-0.5")}>
              <CustomHandle type="source" id="default" color={node.color} isConnecting={isConnecting} isConnected={isConnected} />
              {props.data.on_error === "fail_branch" && (
                <CustomHandle
                  type="source"
                  color={"red"}
                  isConnecting={isConnecting}
                  isConnected={isConnected}
                  errorHandle
                  id="error_handle"
                />
              )}
            </div>
          </div>
        )}
        {props.data.invalid_tip && (
          <Tooltip
            label={props.data.invalid_tip}
            color="#E81332"
            className="!max-w-36 text-center text-[10px] font-semibold uppercase"
            multiline
          >
            <div className="absolute top-1 -left-1 text-red-500">
              <IconMageExclamationCircleFill
                className={cn(
                  "size-6 rounded-full bg-background",
                  snap.scenario.editing.highlightNode === props.id && "animate-jump animate-duration-500",
                )}
              />
            </div>
          </Tooltip>
        )}
        {hovered && !isHoveringThisNode && <BaseNodeActionsPanel {...props} onClickNode={onClickNode} />}
      </div>
      {/* <div className={cn("max-w-32 animate-fade-up truncate rounded bg-[rgba(255,255,255,0.9)] px-3 py-1.5 text-center text-[13px]")}> */}
      <div onClick={onClickNode} className={cn("max-w-32 animate-fade-up truncate rounded px-3 text-center text-[13px]")}>
        {props.data?.label}
      </div>
      <div onClick={onClickNode} className={cn("max-w-32 animate-fade-up truncate rounded px-3 text-center text-[13px]")}>
        #{props.id}
      </div>
    </div>
  )
}

export default BaseNode

//  █████╗ ██╗   ██╗██╗  ██╗
// ██╔══██╗██║   ██║╚██╗██╔╝
// ███████║██║   ██║ ╚███╔╝
// ██╔══██║██║   ██║ ██╔██╗
// ██║  ██║╚██████╔╝██╔╝ ██╗
// ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝
//
const BaseNodeActionsPanel = (
  props: NodeProps<BaseNodeType> & {
    draggingMode?: boolean
    style?: React.CSSProperties
    className?: string
    onClickNode?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  },
) => {
  const { deleteElements } = useReactFlow()
  const nodeDef = store.scenario.nodeDefinitions.record[props.data.node_type]
  const isTrigger = nodeDef.category === NodeCategory.TRIGGER || props.data?.action?.startsWith("trigger::")

  return (
    <div className={cn("absolute top-0 z-[-1] mx-auto w-auto", isTrigger ? "left-2" : "-left-2")}>
      <div className="-mt-12 pb-12">
        <PanelCard
          className={
            "group h-auto animate-fade-up-short gap-0.5 px-1 py-1 [&_path]:stroke-[1.8] [&_svg]:!size-[18px] [&_svg]:!text-fg-secondary"
          }
        >
          <PanelButton
            label="Edit"
            tooltipPosition="top"
            onClick={(e) => {
              props.onClickNode?.(e)
            }}
            className="!size-7 [&_svg]:size-5"
          >
            <IconHugeiconsEdit01 />
          </PanelButton>
          {nodeDef.category !== NodeCategory.TRIGGER && (
            <>
              <Divider orientation="vertical" className="my-1.5 opacity-50" />
              <PanelButton
                label="Duplicate"
                tooltipPosition="top"
                onClick={(e) => {
                  e?.stopPropagation()
                  e?.preventDefault()
                  store.scenario.actions.duplicateNodes([
                    { ...props, position: { x: props.positionAbsoluteX, y: props.positionAbsoluteY } },
                  ])
                }}
                className="!size-7 [&_svg]:size-5"
              >
                <IconHugeiconsCopy02 />
              </PanelButton>
            </>
          )}
          <Divider orientation="vertical" className="my-1.5 opacity-50" />
          <PanelButton
            label="Delete"
            tooltipPosition="top"
            onClick={(e) => {
              e?.stopPropagation()
              e?.preventDefault()
              handleNodesRemove([props.id], deleteElements)
            }}
            className="!size-7 [&_svg]:size-5"
          >
            <IconHugeiconsCancel01 />
          </PanelButton>
        </PanelCard>
      </div>
    </div>
  )
}
